import {
    Panel, Label, Image, Button, DataDisplayEvent,
    DataSourceValidationEvent, ValidationResult, Layout, TableRow, Tabset, Table, TableRowDisplayEvent,
    Textbox, Location, CityState
} from "@mcleod/components";
import { ModelRow, HorizontalAlignment, StringUtil } from "@mcleod/core";
import { BRLTLOrder } from "@mcleod/dispatch/src/brltl/BrltlUtil";
import { ImageName, ImageNameUtil } from "@mcleod/images";

/**
 * Utility functions for billing-related operations shared between EdiBilling and Billing classes
 */

/**
 * Determines if an order is a spot order based on its status
 * @param data - ModelRow containing order data or string status
 * @returns true if order is a spot order (status "S" or "C")
 */
export function isSpotOrder(data: ModelRow | string): boolean {
    let status: string;
    if (data instanceof ModelRow) {
        status = data.get("subject_order_status");
    } else {
        status = data;
    }
    return new Set(["S", "C"]).has(status);
}

/**
 * Filters movement data to only include available moves (status "A")
 * @param data - Array of movement data
 * @returns Filtered data or null if no available moves
 */
export function filterAvailableMoves(data: any): any {
    const filteredData = data.filter(row => row.status === "A");
    return filteredData.length > 0 ? filteredData : null;
}

/**
 * Creates a pending label component with standard styling
 * @returns Label component configured for pending status
 */
export function createPendingLabel(): Label {
    const pendingLabel = new Label({
        id: "pendingLabel",
        text: "Pending",
        backgroundColor: "warning",
        color: "warning.reverse",
        borderWidth: 1,
        borderRadius: 18,
        marginLeft: 10,
        marginRight: 10,
        fontSize: "small",
        paddingLeft: 20,
        paddingRight: 20,
        width: 110,
        fontBold: true,
        rowBreak: false
    });
    pendingLabel.align = HorizontalAlignment.CENTER;
    return pendingLabel;
}

/**
 * Creates a movement time label with movement schedule data
 * @param movement - ModelRow containing movement data
 * @returns Label component with movement time information
 */
export function createMovementTimeLabel(movement: ModelRow<any>): Label {
    const timeLabel = new Label({
        id: "movement_pickup_dates",
        text: movement?.get("movement_sched_arrive_early"),
        fontSize: "medium",
        color: "subtle.dark",
        fontBold: true,
        width: 130,
        rowBreak: false
    });
    timeLabel.align = HorizontalAlignment.CENTER;
    return timeLabel;
}

/**
 * Creates a posted movement time label with unique ID
 * @param movement - ModelRow containing movement data
 * @returns Label component with movement time information and unique ID
 */
export function createPostedMovementTimeLabel(movement: ModelRow<any>): Label {
    const timeLabel = new Label({
        id: `${movement?.get("id")}-movement_pickup_dates`,
        text: movement?.get("movement_sched_arrive_early"),
        fontSize: "medium",
        color: "subtle.dark",
        fontBold: true,
        width: 130,
        rowBreak: false
    });
    timeLabel.align = HorizontalAlignment.CENTER;
    return timeLabel;
}

/**
 * Adds a pending label to a tab panel with tooltip callback
 * @param tab - Panel to add the label to
 * @param movement - ModelRow with movement data
 * @param setTooltipContent - Function to set tooltip callback on the label
 */
export function addPendingLabelToTab(
    tab: Panel,
    movement: ModelRow,
    setTooltipContent: (label: Label, movement: ModelRow) => void
): void {
    const newPendingLabel = createPendingLabel();
    setTooltipContent(newPendingLabel, movement);
    tab.add(newPendingLabel);
}

/**
 * Adds movement pickup times label to a tab
 * @param tab - Panel to add the label to
 * @param movement - ModelRow with movement data
 */
export function addMovementPickupTimes(tab: Panel, movement: ModelRow<any>): void {
    const newTimeLabel = createPostedMovementTimeLabel(movement);
    tab.add(newTimeLabel);
}

/**
 * Adds a split image to a tab based on movement data
 * @param tab - Panel to add the image to
 * @param movement - ModelRow with movement data containing split_image
 */
export function addSplitImageToTab(tab: Panel, movement: ModelRow): void {
     const splitImageName = ImageNameUtil.fromString(movement.get("split_image"));
        const imageSplit = new Image({
            name: splitImageName,
            color: splitImageName == ImageName.ysplitPick ? "success" : "primary",
            marginLeft: 8
        });
        tab.add(imageSplit);
}

/**
 * Shows or hides hazmat warning styling on commodity components
 * @param panelCommodityHazmat - Panel to apply hazmat styling to
 * @param labelHazmatCommodity - Label to show/hide for hazmat warning
 * @param hazmat - Boolean indicating if commodity is hazmat
 */
export function showHazmatWarning(
    panelCommodityHazmat: Panel,
    labelHazmatCommodity: Label,
    hazmat: boolean
): void {
    panelCommodityHazmat.borderWidth = hazmat ? 1 : 0;
    panelCommodityHazmat.borderColor = hazmat ? "error" : "";
    labelHazmatCommodity.visible = hazmat === true;
}

/**
 * Creates BRLTL order data object from active row
 * @param activeRow - ModelRow containing order data
 * @returns BRLTLOrder object with order type, revenue code, and mode
 */
export function getBRLTLData(activeRow: ModelRow): BRLTLOrder {
    const orderData: BRLTLOrder = {
        order_type_id: activeRow?.get("order_type_id"),
        revenue_code_id: activeRow?.get("revenue_code_id"),
        order_mode: activeRow?.get("order_mode")
    };
    return orderData;
}

/**
 * Sets color for "Add Another Stop" button
 * @param event - DataDisplayEvent from button
 */
export function buttonAddAnotherStopOnDataDisplay(event: DataDisplayEvent): void {
    (event.target as Button).setProps({
        ...(event.target as Button).allProps,
        "color": "primary"
    });
}

/**
 * Validates that stops data meets minimum requirements before save
 * @param event - DataSourceValidationEvent
 * @param stopData - Array of stop data
 * @param stopsTable - Table component for validation error targeting
 */
export function validateStopsBeforeSave(
    event: DataSourceValidationEvent,
    stopData: ModelRow[],
    stopsTable: any
): void {
    let errorMessage = null;
    if (stopData.length < 2) {
        errorMessage = "An order must have at least two stops.";
    }
    else if (!stopData.some(stop => stop.get("stop_type") === "PU")) {
        errorMessage = "An order must have at least one pickup stop.";
    }
    else if (!stopData.some(stop => stop.get("stop_type") === "SO")) {
        errorMessage = "An order must have at least one delivery stop.";
    }
    else if (stopData[stopData.length - 1].get("stop_type") !== "SO") {
        errorMessage = "The last stop must be a delivery stop.";
    }
    if (errorMessage) {
        event.validationResults.push({
            isValid: false,
            validationMessage: errorMessage,
            caption: errorMessage,
            component: stopsTable
        });
    }
}

/**
 * Disables all components in a layout for credit orders
 * @param component - Root component to disable (Layout, TableRow, or Tabset)
 * @param ignoreList - Array of component IDs to ignore during disabling
 */
export function disableCreditFields(
    component: Layout | TableRow | Tabset,
    ignoreList: string[] = ['tableImages', 'buttonMoveDetails', 'tabsetAddOrders', 'tabsetStopNotesRefNos']
): void {
    component.forEveryChildComponent(comp => {
        if (ignoreList.includes(comp.id))
            return;

        comp.enabled = false;

        if (comp instanceof Table) {
            const nestedTable: Table = comp as Table;
            nestedTable.addRowDisplayListener((event: TableRowDisplayEvent) => {
                const row: TableRow = (event as TableRowDisplayEvent).getTableRow();
                row.forEveryChildComponent(child => {
                    child.enabledDuringUpdate = false;
                });
            });
        }
    });
}

/**
 * Makes adjustments for spot orders (disables certain requirements)
 * @param activeRow - Current active row data
 * @param textboxCustomerId - Customer ID textbox to modify requirements
 */
export function makeSubjectOrderAdjustments(activeRow: ModelRow, textboxCustomerId: any): void {
    if (isSpotOrder(activeRow)) {
        textboxCustomerId.required = false;
    }
}

/**
 * Enables or disables on-hold components
 * @param enabled - Whether components should be enabled
 * @param switchHoldLoad - Switch component for hold load
 * @param textboxHoldReason - Textbox for hold reason
 */
export function enableOnHoldComponents(
    enabled: boolean,
    switchHoldLoad: any,
    textboxHoldReason: any
): void {
    switchHoldLoad.enabled = enabled;
    textboxHoldReason.enabled = enabled;
}

/**
 * Removes child data sources from stops table rows
 * @param stopsTableRows - Array of table rows to clean up
 */
export function removeChildDataSources(stopsTableRows: TableRow[]): void {
    stopsTableRows?.forEach(tableRow => {
        const orderStop = tableRow["orderStop"];
        if (orderStop != null) {
            orderStop.commentsAndRefNbrs.sourceStopNote.parentDataSource = null;
            orderStop.commentsAndRefNbrs.sourceReferenceNumber.parentDataSource = null;
        }
    });
}

/**
 * Updates billing distance label colors and visibility based on rate textbox color
 * @param layoutOverview - Overview layout containing distance labels
 * @param rateLayout - Rate layout containing distance textbox
 */
export function updateLabelBillingDistance(layoutOverview: any, rateLayout: any): void {
    layoutOverview.labelBillingDistance.color = rateLayout.textboxBillDistance.color;
    layoutOverview.labelBillDistanceUm.color = rateLayout.textboxBillDistance.color;
    const components = layoutOverview.panelBillingDistance.components;

    const isWarning = layoutOverview.labelBillingDistance.color === "warning";
    layoutOverview.billingDistanceDangerImage.visible = isWarning;
    for (const component of components) {
        const label = component as Label;
        if (!StringUtil.isEmptyString(label.field)) {
            label.color = isWarning ? "warning" : "default";
        }
        label.allowTooltip = !isWarning;
    }
}

/**
 * Enables captions for textbox components within a container
 * @param comp - Component container to process
 */
export function enableCaptions(comp: any): void {
    comp.forEveryChildComponent(c => {
        let textbox: Textbox;
        if (c instanceof Textbox) {
            textbox = c;
        } else if (c instanceof Location || c instanceof CityState) {
            textbox = c.textCombined;
        }
        if (textbox) {
            textbox.captionVisibleInsideTable = true;
            textbox.captionVisible = true;
        }
    });
}

/**
 * Checks if contact info was filled from the portal
 * @param data - ModelRow containing contact data
 * @returns true if portal contact info exists
 */
export function hasPortalContactInfo(data: ModelRow): boolean {
    //This is needed to check if contact info was filled from the portal.
    //If it was filled from portal, we should not automatically update
    //if the location ID has contacts.
    return !data.isNull("phone") || !data.isNull("contact_name");
}
